/**
 * Browser-side Pyodide loader.
 *
 * Pyodide ships ~10 MB of WASM + data files. Rather than bundling those through
 * Vite, we load the runtime from the official jsDelivr CDN at startup. The CDN
 * version is pinned to match the `pyodide` dev dependency used by the test
 * suite, so the headless tests exercise the same interpreter the browser runs.
 */
import type { LoadPyodideFn, LoadPyodideOptions, PyodideLike } from "./types";

/** Pinned Pyodide release. Keep in sync with the `pyodide` version in package.json. */
export const PYODIDE_VERSION = "0.27.2";

/** Base URL for the pinned Pyodide distribution on jsDelivr. */
export const PYODIDE_CDN_INDEX_URL = `https://cdn.jsdelivr.net/pyodide/v${PYODIDE_VERSION}/full/`;

// Pyodide attaches `loadPyodide` to the global scope once its loader script runs.
declare global {
  interface Window {
    loadPyodide?: LoadPyodideFn;
  }
}

let scriptPromise: Promise<LoadPyodideFn> | null = null;

/**
 * Inject the Pyodide loader `<script>` (once) and resolve with the global
 * `loadPyodide` factory it exposes. Subsequent calls reuse the same promise.
 */
function ensureLoaderScript(): Promise<LoadPyodideFn> {
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise<LoadPyodideFn>((resolve, reject) => {
    if (window.loadPyodide) {
      resolve(window.loadPyodide);
      return;
    }

    const script = document.createElement("script");
    script.src = `${PYODIDE_CDN_INDEX_URL}pyodide.js`;
    script.async = true;
    script.onload = () => {
      if (window.loadPyodide) {
        resolve(window.loadPyodide);
      } else {
        reject(new Error("Pyodide loaded but window.loadPyodide is undefined"));
      }
    };
    script.onerror = () =>
      reject(new Error(`Failed to load Pyodide from ${script.src}`));
    document.head.appendChild(script);
  });

  return scriptPromise;
}

/**
 * Browser implementation of the {@link LoadPyodideFn} contract. Suitable to
 * pass straight into `createPythonRuntime({ loadPyodide: loadPyodideFromCdn })`.
 */
export async function loadPyodideFromCdn(
  options?: LoadPyodideOptions,
): Promise<PyodideLike> {
  const factory = await ensureLoaderScript();
  return factory({ indexURL: PYODIDE_CDN_INDEX_URL, ...options });
}
